/**
 * KiekkoKingi section controller: store-gallery
 * Kept external so the browser can defer and cache this code.
 */
(() => {
  const kkScript = document.currentScript;
  if (!kkScript) return;

  (() => {
          const rootId = kkScript.dataset.rootId;
          const initGallery = (root) => {
            if (!root || root.dataset.kkGalleryReady === 'true') return;
  
            root.dataset.kkGalleryReady = 'true';
            root.classList.add('is-js-enabled');
  
            const cleanups = [];
            const listen = (target, eventName, handler, options) => {
              if (!target) return;
              target.addEventListener(eventName, handler, options);
              cleanups.push(() => target.removeEventListener(eventName, handler, options));
            };
  
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
            const track = root.querySelector('[data-gallery-track]');
            const cards = track ? Array.from(track.querySelectorAll('[data-gallery-card]')) : [];
            const dots = Array.from(root.querySelectorAll('[data-gallery-dot]'));
            const currentCounter = root.querySelector('[data-gallery-current]');
            const progress = root.querySelector('[data-gallery-progress]');
            let activeCard = 0;
            let scrollFrame = 0;
  
            const updateIndicator = (index) => {
              if (!cards.length) return;
              activeCard = Math.max(0, Math.min(index, cards.length - 1));
              dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeCard));
              if (currentCounter) currentCounter.textContent = String(activeCard + 1);
              if (progress) progress.style.transform = `scaleX(${(activeCard + 1) / cards.length})`;
            };
  
            const findActiveCard = () => {
              if (!track || !cards.length) return;
              const trackLeft = track.getBoundingClientRect().left;
              let closestIndex = 0;
              let closestDistance = Infinity;
              cards.forEach((card, index) => {
                const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestIndex = index;
                }
              });
              updateIndicator(closestIndex);
            };
  
            const onTrackScroll = () => {
              if (scrollFrame) return;
              scrollFrame = window.requestAnimationFrame(() => {
                scrollFrame = 0;
                findActiveCard();
              });
            };
  
            if (track && cards.length > 1) {
              listen(track, 'scroll', onTrackScroll, { passive: true });
              listen(window, 'resize', findActiveCard, { passive: true });
              dots.forEach((dot, index) => {
                listen(dot, 'click', () => {
                  const card = cards[index];
                  if (!card) return;
                  track.scrollTo({ left: card.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
                });
              });
              updateIndicator(0);
            }
  
            const contentToggles = Array.from(root.querySelectorAll('[data-gallery-content-toggle]'));
            contentToggles.forEach((toggle) => {
              listen(toggle, 'click', () => {
                const card = toggle.closest('[data-gallery-card]');
                if (!card) return;
                const willOpen = !card.classList.contains('is-content-open');
                cards.forEach((otherCard) => {
                  if (otherCard === card) return;
                  otherCard.classList.remove('is-content-open');
                  const otherToggle = otherCard.querySelector('[data-gallery-content-toggle]');
                  if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
                });
                card.classList.toggle('is-content-open', willOpen);
                toggle.setAttribute('aria-expanded', String(willOpen));
                toggle.setAttribute('aria-label', willOpen ? (root.dataset.hideDetailsLabel || '') : (root.dataset.showDetailsLabel || ''));
              });
            });
  
            let entryObserver = null;
            if (root.dataset.entryAnimation === 'true' && 'IntersectionObserver' in window && !reducedMotion.matches) {
              entryObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting) return;
                  root.classList.add('is-visible');
                  entryObserver.disconnect();
                });
              }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
              entryObserver.observe(root);
            } else {
              root.classList.add('is-visible');
            }
  
            const lightbox = root.querySelector('[data-gallery-lightbox]');
            const sectionHost = root.closest('[id^="shopify-section-"]');
            const pageMain = root.closest('.site-main');
            const triggers = Array.from(root.querySelectorAll('[data-gallery-lightbox-trigger]'));
            const lightboxImage = lightbox ? lightbox.querySelector('[data-gallery-lightbox-image]') : null;
            const lightboxTitle = lightbox ? lightbox.querySelector('[data-gallery-lightbox-title]') : null;
            const lightboxCaption = lightbox ? lightbox.querySelector('[data-gallery-lightbox-caption]') : null;
            const lightboxCounter = lightbox ? lightbox.querySelector('[data-gallery-lightbox-counter]') : null;
            const lightboxMeta = lightbox ? lightbox.querySelector('[data-gallery-lightbox-meta]') : null;
            const closeButton = lightbox ? lightbox.querySelector('[data-gallery-lightbox-close]') : null;
            const previousButton = lightbox ? lightbox.querySelector('[data-gallery-lightbox-previous]') : null;
            const nextButton = lightbox ? lightbox.querySelector('[data-gallery-lightbox-next]') : null;
            const backdropButton = lightbox ? lightbox.querySelector('[data-gallery-lightbox-backdrop]') : null;
            const stage = lightbox ? lightbox.querySelector('[data-gallery-lightbox-stage]') : null;
            const galleryContent = root.querySelector('[data-gallery-content]');
            let lightboxOpen = false;
            let currentLightboxIndex = 0;
            let lastTrigger = null;
            let closeTimer = 0;
            let savedScrollY = 0;
            let savedBodyStyles = null;
            let savedContentAriaHidden = null;
            let touchStartX = 0;
            let touchStartY = 0;
  
            const setLightboxLayerActive = (isActive) => {
              root.classList.toggle('is-lightbox-open', isActive);
              if (sectionHost) sectionHost.classList.toggle('kk-gallery-lightbox-host-open', isActive);
              if (pageMain) {
                const hasOpenGallery = Boolean(pageMain.querySelector('[data-kks="ga"].is-lightbox-open'));
                pageMain.classList.toggle('kk-gallery-lightbox-main-open', hasOpenGallery);
              }
            };
  
            const getFocusable = () => {
              if (!lightbox) return [];
              return Array.from(lightbox.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'))
                .filter((element) => element.getAttribute('tabindex') !== '-1' && !element.hidden && element.getClientRects().length > 0);
            };
  
            const lockPage = () => {
              const body = document.body;
              savedScrollY = window.scrollY || window.pageYOffset;
              savedBodyStyles = {
                position: body.style.position,
                top: body.style.top,
                left: body.style.left,
                right: body.style.right,
                width: body.style.width,
                overflow: body.style.overflow,
                paddingRight: body.style.paddingRight
              };
              const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
              body.style.position = 'fixed';
              body.style.top = `-${savedScrollY}px`;
              body.style.left = '0';
              body.style.right = '0';
              body.style.width = '100%';
              body.style.overflow = 'hidden';
              if (scrollbarWidth) body.style.paddingRight = `${scrollbarWidth}px`;
            };
  
            const unlockPage = () => {
              if (!savedBodyStyles) return;
              const body = document.body;
              Object.entries(savedBodyStyles).forEach(([property, value]) => {
                body.style[property] = value;
              });
              savedBodyStyles = null;
              window.scrollTo(0, savedScrollY);
            };
  
            const isolateBackground = (shouldIsolate) => {
              if (!galleryContent) return;
              if (shouldIsolate) {
                savedContentAriaHidden = galleryContent.getAttribute('aria-hidden');
                galleryContent.setAttribute('aria-hidden', 'true');
                galleryContent.setAttribute('inert', '');
              } else {
                galleryContent.removeAttribute('inert');
                if (savedContentAriaHidden === null) galleryContent.removeAttribute('aria-hidden');
                else galleryContent.setAttribute('aria-hidden', savedContentAriaHidden);
                savedContentAriaHidden = null;
              }
            };
  
            const renderLightbox = () => {
              const trigger = triggers[currentLightboxIndex];
              if (!trigger || !lightboxImage) return;
              const useMobile = window.matchMedia('(max-width: 719px)').matches;
              const source = useMobile && trigger.dataset.lightboxMobileSrc
                ? trigger.dataset.lightboxMobileSrc
                : trigger.dataset.lightboxSrc;
              const title = trigger.dataset.lightboxTitle || '';
              const caption = trigger.dataset.lightboxCaption || '';
  
              lightboxImage.src = source;
              lightboxImage.alt = trigger.dataset.lightboxAlt || title;
              if (lightboxTitle) {
                lightboxTitle.textContent = title;
                lightboxTitle.hidden = !title;
              }
              if (lightboxCaption) {
                lightboxCaption.textContent = caption;
                lightboxCaption.hidden = !caption;
              }
              if (lightboxCounter) lightboxCounter.textContent = `${currentLightboxIndex + 1} / ${triggers.length}`;
              if (lightboxMeta) {
                const hasCopy = Boolean((lightboxTitle && title) || (lightboxCaption && caption));
                lightboxMeta.hidden = !hasCopy && !lightboxCounter;
              }
            };
  
            const openLightbox = (index, trigger) => {
              if (!lightbox || !triggers.length) return;
              if (lightboxOpen) return;
              document.dispatchEvent(new CustomEvent('kk:store-gallery:lightbox-open', { detail: { root } }));
              window.clearTimeout(closeTimer);
              currentLightboxIndex = Math.max(0, Math.min(index, triggers.length - 1));
              lastTrigger = trigger || triggers[currentLightboxIndex];
              renderLightbox();
              lightbox.hidden = false;
              lightbox.setAttribute('aria-hidden', 'false');
              setLightboxLayerActive(true);
              lightboxOpen = true;
              lockPage();
              isolateBackground(true);
              window.requestAnimationFrame(() => {
                if (!lightboxOpen) return;
                lightbox.classList.add('is-open');
                if (closeButton) closeButton.focus({ preventScroll: true });
              });
            };
  
            const closeLightbox = (restoreFocus = true) => {
              if (!lightbox || !lightboxOpen) return;
              lightboxOpen = false;
              lightbox.classList.remove('is-open');
              lightbox.setAttribute('aria-hidden', 'true');
              isolateBackground(false);
              unlockPage();
              if (restoreFocus && lastTrigger && document.documentElement.contains(lastTrigger)) {
                lastTrigger.focus({ preventScroll: true });
              }
              const finishClose = () => {
                if (lightboxOpen) return;
                lightbox.hidden = true;
                setLightboxLayerActive(false);
                if (lightboxImage) {
                  lightboxImage.removeAttribute('src');
                  lightboxImage.alt = '';
                }
              };
              if (reducedMotion.matches) finishClose();
              else closeTimer = window.setTimeout(finishClose, Number.parseInt(getComputedStyle(root).getPropertyValue('--kk-gallery-lightbox-speed'), 10) || 350);
            };
  
            const showPrevious = () => {
              if (!triggers.length) return;
              currentLightboxIndex = (currentLightboxIndex - 1 + triggers.length) % triggers.length;
              renderLightbox();
            };
  
            const showNext = () => {
              if (!triggers.length) return;
              currentLightboxIndex = (currentLightboxIndex + 1) % triggers.length;
              renderLightbox();
            };

            listen(document, 'kk:store-gallery:lightbox-open', (event) => {
              if (!lightboxOpen || !event.detail || event.detail.root === root) return;
              closeLightbox(false);
            });
  
            triggers.forEach((trigger, index) => {
              listen(trigger, 'click', (event) => {
                const mobileDisabled = window.matchMedia('(max-width: 719px)').matches && root.dataset.mobileLightbox !== 'true';
                if (mobileDisabled) return;
                event.preventDefault();
                openLightbox(index, trigger);
              });
            });
  
            listen(closeButton, 'click', () => closeLightbox());
            listen(backdropButton, 'click', () => closeLightbox());
            listen(previousButton, 'click', showPrevious);
            listen(nextButton, 'click', showNext);
  
            listen(document, 'keydown', (event) => {
              if (!lightboxOpen) return;
              if (event.key === 'Escape') {
                event.preventDefault();
                closeLightbox();
                return;
              }
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                showPrevious();
                return;
              }
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                showNext();
                return;
              }
              if (event.key !== 'Tab') return;
  
              const focusable = getFocusable();
              if (!focusable.length) {
                event.preventDefault();
                return;
              }
              const first = focusable[0];
              const last = focusable[focusable.length - 1];
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            });
  
            listen(stage, 'touchstart', (event) => {
              if (!lightboxOpen || event.touches.length !== 1) return;
              touchStartX = event.touches[0].clientX;
              touchStartY = event.touches[0].clientY;
            }, { passive: true });
  
            listen(stage, 'touchend', (event) => {
              if (!lightboxOpen || !event.changedTouches.length || triggers.length < 2) return;
              const deltaX = event.changedTouches[0].clientX - touchStartX;
              const deltaY = event.changedTouches[0].clientY - touchStartY;
              if (Math.abs(deltaX) < 52 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
              if (deltaX > 0) showPrevious();
              else showNext();
            }, { passive: true });
  
            const onBlockSelect = (event) => {
              if (!event.detail || !event.detail.blockId) return;
              const card = root.querySelector(`[data-block-id="${event.detail.blockId}"]`);
              if (!card) return;
              card.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
            };
  
            const destroyGallery = () => {
              closeLightbox(false);
              window.clearTimeout(closeTimer);
              setLightboxLayerActive(false);
              if (lightbox) {
                lightbox.classList.remove('is-open');
                lightbox.hidden = true;
                lightbox.setAttribute('aria-hidden', 'true');
              }
              if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
              if (entryObserver) entryObserver.disconnect();
              document.removeEventListener('shopify:section:load', onSectionLoad);
              cleanups.splice(0).forEach((cleanup) => cleanup());
              delete root.dataset.kkGalleryReady;
            };
  
            const onSectionUnload = (event) => {
              if (event.target === root || (event.target && event.target.contains && event.target.contains(root))) {
                destroyGallery();
              }
            };
  
            listen(document, 'shopify:block:select', onBlockSelect);
            listen(document, 'shopify:section:unload', onSectionUnload);
          };
  
          const initialRoot = document.getElementById(rootId);
          initGallery(initialRoot);
  
          const onSectionLoad = (event) => {
            const candidate = event.target && event.target.id === rootId
              ? event.target
              : event.target && event.target.querySelector
                ? event.target.querySelector(`#${CSS.escape(rootId)}`)
                : null;
            if (candidate) initGallery(candidate);
          };
  
          document.addEventListener('shopify:section:load', onSectionLoad);
        })();
})();
